import { CalendarRange } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface ReportPeriodFilterProps {
  onPeriodChange?: (period: string) => void;
  selectedPeriod: string;
}

const periods = [
  { value: 'tous', label: 'Toutes les périodes' },
  { value: '7-jours', label: 'Les 7 derniers jours' },
  { value: '30-jours', label: 'Les 30 derniers jours' },
  { value: '90-jours', label: 'Les 90 derniers jours' },
];

const triggerClassName =
  'inline-flex h-10 items-center gap-2 rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white px-3.5 text-[13px] font-medium text-[hsl(var(--app-text-strong))] transition-colors hover:bg-[hsl(var(--app-surface-muted))] whitespace-nowrap';

const dropdownClassName =
  'absolute top-full left-0 z-50 mt-2 min-w-max overflow-hidden rounded-[16px] border border-[hsl(var(--app-border-subtle))] bg-white p-1 shadow-[0_18px_38px_rgba(15,23,42,0.08)]';

const itemClassName =
  'flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2 text-[13px] text-[hsl(var(--app-text-strong))] transition-colors hover:bg-[hsl(var(--app-surface-muted))] whitespace-nowrap';

export const ReportPeriodFilter = ({
  onPeriodChange,
  selectedPeriod,
}: ReportPeriodFilterProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePeriodChange = (value: string) => {
    setDropdownOpen(false);
    onPeriodChange?.(value);
  };

  const getDisplayLabel = () => {
    const period = periods.find((item) => item.value === selectedPeriod);
    return period?.label || 'Toutes les périodes';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setDropdownOpen((open) => !open)}
        className={triggerClassName}
      >
        <CalendarRange className="h-4 w-4 text-[hsl(var(--app-text-muted))]" />
        <span>{getDisplayLabel()}</span>
        <ChevronDownIcon />
      </button>

      {dropdownOpen && (
        <div className={dropdownClassName}>
          {periods.map((period) => (
            <div
              key={period.value}
              onClick={() => handlePeriodChange(period.value)}
              className={`${itemClassName} ${selectedPeriod === period.value ? 'bg-[hsl(var(--app-surface-muted))]' : ''}`}
            >
              <div className={selectedPeriod === period.value ? 'text-[hsl(var(--app-accent))]' : ''}>
                {period.label}
              </div>
              {selectedPeriod === period.value && <CheckIcon />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.0625 6.5625L4.6875 9.1875L9.9375 3.5625"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.5 5.25L7 8.75L10.5 5.25"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
